/**
 * admin/capabilities — framework-agnostic RBAC role → capability map.
 *
 * Mirrors the backend capability gating (campaign #18, Track B). The back-office
 * UI uses this to (a) decide whether a user may enter the admin area at all and
 * (b) hide/disable individual actions a role can't perform. The backend remains
 * the source of truth and re-checks every mutation; this is purely a UX layer so
 * the client never offers an action that would 403.
 *
 * The model has to exist in TWO runtimes (Rust decides, the UI hides what Rust
 * will reject), so there is no way to have a single implementation — but there
 * IS a single CONTRACT: `rbac-contract.json`. Rust embeds it with `include_str!`
 * and asserts its map against it; this module derives role ranks from it at
 * runtime and asserts the same. Touching one side without the other turns that
 * side's test red. Before, each side only checked itself — which is exactly how
 * the backend drifted into being MORE permissive than the UI without anything
 * noticing (it collapsed every user action into one fat `ManageUsers`, so
 * `support` could change roles and `admin` could GDPR-delete accounts).
 *
 * Roles (broad → narrow):
 *   - super_admin — everything, including destructive GDPR removals + role grants
 *   - admin       — manage users/flags, but not destructive removals or granting
 *                   privileged roles
 *   - support     — read users + audit, ban/unban + revoke sessions + reset MFA,
 *                   manage content (no role/flag changes, no removals)
 *   - read_only   — read-only access to every admin panel
 *   - user        — no admin access
 */

import contract from "./rbac-contract.json";

export const ADMIN_ROLES = ["super_admin", "admin", "support", "read_only", "user"] as const;

export type AdminRole = (typeof ADMIN_ROLES)[number];

/** Discrete capabilities the back-office UI gates on. */
export type AdminCapability =
  | "admin.access"
  | "users.read"
  | "users.setRole"
  | "users.ban"
  | "users.revokeSessions"
  | "users.resetMfa"
  | "users.remove"
  | "audit.read"
  | "integrations.read"
  | "integrations.testEmail"
  | "flags.read"
  | "flags.write"
  | "metrics.read"
  | "content.read"
  | "content.write";

/**
 * Capabilities deliberately absent from the contract, with the reason in
 * writing. Anything else missing makes `capabilities.test.ts` fail — adding a
 * capability must force a DECISION, not allow silence.
 */
export const CONTRACT_EXEMPT_CAPABILITIES: Readonly<Record<string, string>> = {
  "admin.access":
    "Derived, not granted: it means 'has at least one back-office capability'. " +
    "The server equivalent is `has_any_admin_capability`, which is also derived " +
    "from the role rather than being an entry in the capability map.",
};

const READ_CAPS: AdminCapability[] = [
  "admin.access",
  "users.read",
  "audit.read",
  "integrations.read",
  "flags.read",
  "metrics.read",
  "content.read",
];

const SUPPORT_CAPS: AdminCapability[] = [
  ...READ_CAPS,
  "users.ban",
  "users.revokeSessions",
  "users.resetMfa",
  "integrations.testEmail",
  "content.write",
];

const ADMIN_CAPS: AdminCapability[] = [...SUPPORT_CAPS, "users.setRole", "flags.write"];

const SUPER_ADMIN_CAPS: AdminCapability[] = [...ADMIN_CAPS, "users.remove"];

const CAPABILITIES_BY_ROLE: Record<AdminRole, AdminCapability[]> = {
  super_admin: SUPER_ADMIN_CAPS,
  admin: ADMIN_CAPS,
  support: SUPPORT_CAPS,
  read_only: READ_CAPS,
  user: [],
};

/**
 * Role ranks, DERIVED from the shared contract at runtime (not re-typed here).
 * Re-typing them is how the two runtimes drift apart.
 */
export const ROLE_RANK: Readonly<Record<AdminRole, number>> = Object.freeze({
  super_admin: contract.roles.super_admin,
  admin: contract.roles.admin,
  support: contract.roles.support,
  read_only: contract.roles.read_only,
  user: contract.roles.user,
});

function isAdminRole(role: string | null | undefined): role is AdminRole {
  return typeof role === "string" && (ADMIN_ROLES as readonly string[]).includes(role);
}

/** Narrows an arbitrary string (e.g. `session.user.role`) to a known role. */
export function toAdminRole(role: string | null | undefined): AdminRole {
  return isAdminRole(role) ? role : "user";
}

/** Returns the capability set for a role string (unknown → `user` → none). */
export function capabilitiesForRole(role: string | null | undefined): ReadonlySet<AdminCapability> {
  return new Set(CAPABILITIES_BY_ROLE[toAdminRole(role)]);
}

/** True when the role grants the given capability. */
export function roleCan(role: string | null | undefined, capability: AdminCapability): boolean {
  return capabilitiesForRole(role).has(capability);
}

/** True when the role may enter the admin back-office at all. */
export function isAdminCapable(role: string | null | undefined): boolean {
  return roleCan(role, "admin.access");
}

/**
 * The roles `actorRole` is allowed to GRANT — mirrors `can_grant_role` in Rust.
 *
 * The signature carries the actor on purpose. The constant this replaced
 * (`ASSIGNABLE_ROLES`, no parameter) never had to say *assignable by whom*, so
 * it was literally the full role list: the role `<select>` offered `super_admin`
 * to every admin, and the server happily accepted it.
 */
export function assignableRolesFor(actorRole: string | null | undefined): AdminRole[] {
  const actor = toAdminRole(actorRole);
  if (actor === "super_admin") {
    return [...ADMIN_ROLES];
  }
  const actorRank = ROLE_RANK[actor];
  return ADMIN_ROLES.filter((r) => ROLE_RANK[r] < actorRank);
}

/**
 * True when `actorRole` outranks `targetRole` enough to act on them — mirrors
 * `can_administer` in Rust. `super_admin` may act on peers (otherwise a rogue
 * super_admin would be untouchable).
 */
export function canAdminister(
  actorRole: string | null | undefined,
  targetRole: string | null | undefined,
): boolean {
  const actor = toAdminRole(actorRole);
  const target = toAdminRole(targetRole);
  if (actor === "super_admin" && target === "super_admin") {
    return true;
  }
  return ROLE_RANK[actor] > ROLE_RANK[target];
}

/** The raw shared contract, for the cross-runtime test. */
export const RBAC_CONTRACT = contract;
